import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  conditionSchema,
  inventoryUnitsResponseSchema,
  type CatalogItem,
  type Condition,
  type IssueLineRequest,
} from '@unit/shared';
import { apiGet } from '../../lib/api.js';
import { itemName } from '../inventory/itemName.js';

const input =
  'rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900 focus:border-slate-500 focus:outline-none';
const secondary =
  'rounded-lg px-3 py-1.5 text-sm text-slate-600 ring-1 ring-slate-300 transition hover:bg-slate-100 disabled:opacity-40';

/** One line as the editor holds it: the catalog item, its unit if serialized, and the rest. */
export interface Line {
  key: number;
  item: CatalogItem;
  unit: { id: string; serialNumber: string } | null;
  quantity: number;
  condition: Condition;
  notes: string;
}

let nextKey = 1;
export function newLine(item: CatalogItem, over: Partial<Omit<Line, 'key' | 'item'>> = {}): Line {
  return { key: nextKey++, item, unit: null, quantity: 1, condition: 'good', notes: '', ...over };
}

/** The request shape the server takes, from the editor's lines. */
export function toRequestLines(lines: Line[]): IssueLineRequest[] {
  return lines.map((l) => ({
    catalogItemId: l.item.id,
    inventoryUnitId: l.unit?.id ?? null,
    quantity: l.item.tracking === 'serialized' ? 1 : l.quantity,
    condition: l.condition,
    notes: l.notes.trim() || null,
  }));
}

/** Every serialized line has its unit chosen, and there is at least one line. */
export function linesComplete(lines: Line[]): boolean {
  return lines.length > 0 && lines.every((l) => l.item.tracking === 'quantity' || l.unit);
}

/**
 * The list of lines with an "add item" picker — shared by the issue wizard,
 * the storeroom's edit of an unsigned form, and form templates, so a line
 * means the same thing everywhere. Serialized items get the scan-friendly
 * serial picker unless `withSerials` is off (a template names no unit).
 */
export function LineEditor({
  lines,
  onChange,
  items,
  withSerials = true,
  testId = 'issue',
}: {
  lines: Line[];
  onChange: (lines: Line[]) => void;
  items: CatalogItem[];
  withSerials?: boolean;
  /** Prefix for the test ids: `<prefix>-lines`, `<prefix>-line`. */
  testId?: string;
}) {
  const { t, i18n } = useTranslation();
  const [adding, setAdding] = useState('');

  const addLine = (id: string) => {
    const item = items.find((i) => i.id === id);
    if (!item) return;
    onChange([...lines, newLine(item)]);
    setAdding('');
  };
  const update = (key: number, patch: Partial<Line>) =>
    onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const remove = (key: number) => onChange(lines.filter((l) => l.key !== key));

  return (
    <>
      <ul className="mt-4 space-y-3" data-testid={`${testId}-lines`}>
        {lines.map((line) => (
          <li key={line.key} className="rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200" data-testid={`${testId}-line`} data-item={line.item.id}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-medium text-slate-900">{itemName(line.item, i18n.language)}</span>
              <button type="button" onClick={() => remove(line.key)} className="text-xs text-slate-500 underline-offset-4 hover:underline" data-testid="line-remove">
                {t('issue.removeLine')}
              </button>
            </div>
            <div className="mt-2 flex flex-wrap items-end gap-3">
              {line.item.tracking === 'serialized' && withSerials ? (
                <SerialPicker
                  catalogItemId={line.item.id}
                  selected={line.unit}
                  onSelect={(unit) => update(line.key, { unit })}
                />
              ) : line.item.tracking === 'quantity' ? (
                <label className="text-xs text-slate-600">
                  {t('issue.quantity')}
                  <input
                    className={`${input} latin-inline mt-1 block w-24`}
                    inputMode="numeric"
                    value={line.quantity}
                    onChange={(e) => update(line.key, { quantity: Math.max(1, Number(e.target.value) || 1) })}
                    data-testid="line-quantity"
                  />
                </label>
              ) : null}
              <label className="text-xs text-slate-600">
                {t('issue.condition')}
                <select className={`${input} mt-1 block`} value={line.condition} onChange={(e) => update(line.key, { condition: e.target.value as Condition })}>
                  {conditionSchema.options.map((c) => (
                    <option key={c} value={c}>{t(`inventory.condition.${c}`)}</option>
                  ))}
                </select>
              </label>
              <label className="grow text-xs text-slate-600">
                {t('issue.notes')}
                <input className={`${input} mt-1 block w-full`} value={line.notes} onChange={(e) => update(line.key, { notes: e.target.value })} />
              </label>
            </div>
          </li>
        ))}
      </ul>

      <label className="mt-4 block text-xs text-slate-600">
        {t('issue.addItem')}
        <select className={`${input} mt-1 block`} value={adding} onChange={(e) => addLine(e.target.value)} data-testid="add-item">
          <option value="">{t('issue.chooseItem')}</option>
          {items.map((i) => (
            <option key={i.id} value={i.id}>
              {itemName(i, i18n.language)} · {t(`inventory.tracking.${i.tracking}`)}
            </option>
          ))}
        </select>
      </label>
    </>
  );
}

/**
 * Serial picker (§3.9 answered "scan-friendly"): a text field that searches
 * in-stock units of this item as you type, and selects on an exact match —
 * a keyboard-wedge scanner types the serial and Enter, and the unit is chosen.
 */
export function SerialPicker({
  catalogItemId,
  selected,
  onSelect,
}: {
  catalogItemId: string;
  selected: { id: string; serialNumber: string } | null;
  onSelect: (unit: { id: string; serialNumber: string } | null) => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [results, setResults] = useState<{ id: string; serialNumber: string }[]>([]);

  useEffect(() => {
    if (selected) return;
    let cancelled = false;
    const handle = setTimeout(() => {
      const q = new URLSearchParams({ catalogItemId, status: 'in_stock', limit: '8' });
      if (text.trim()) q.set('serial', text.trim());
      void apiGet(`/inventory/units?${q.toString()}`, inventoryUnitsResponseSchema).then((r) => {
        if (cancelled) return;
        setResults(r.units);
        const exact = r.units.find((u) => u.serialNumber.toLowerCase() === text.trim().toLowerCase());
        if (exact) onSelect(exact);
      });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [text, catalogItemId, selected, onSelect]);

  if (selected) {
    return (
      <div className="flex items-center gap-2 text-sm" data-testid="serial-selected">
        <span className="latin-inline rounded-lg bg-slate-900 px-2.5 py-1 text-white">{selected.serialNumber}</span>
        <button type="button" onClick={() => { onSelect(null); setText(''); }} className={secondary}>
          {t('issue.changeSerial')}
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <label className="text-xs text-slate-600">
        {t('issue.serial')}
        <input
          className={`${input} latin-inline mt-1 block w-56`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('issue.serialPlaceholder')}
          data-testid="serial-input"
          autoFocus
        />
      </label>
      {results.length > 0 && (
        <ul className="absolute z-10 mt-1 max-h-48 w-56 overflow-auto rounded-lg bg-white shadow-lg ring-1 ring-slate-200" data-testid="serial-results">
          {results.map((u) => (
            <li key={u.id}>
              <button type="button" onClick={() => onSelect(u)} className="latin-inline block w-full px-3 py-1.5 text-start text-sm hover:bg-slate-100">
                {u.serialNumber}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
